import { jsx as _jsx } from "react/jsx-runtime";
/**
 * The four switches every Clavian surface sets, and nothing else.
 *
 *   mode     dark, or follow the OS — Eclipse is dark first, so `system` resolves to dark
 *            unless the surface offers the light theme (BRAND.md §4, ADR-0041)
 *   accent   brand — the one accent family on every surface (§4, ADR-0060)
 *   density  comfortable, or kiosk — read from a gaming chair (spec/12 §1)
 *   motion   auto, or none — what the kiosk sets while a session is running (spec/12 §15)
 *
 * They are data attributes, not props threaded through components, because the tokens they select
 * are CSS variables: setting them on a wrapper re-themes everything inside it, including anything
 * an app renders with Tailwind utilities.
 *
 * The provider can sit on a subtree, so the console can preview a venue's brand next to its own
 * chrome, and the gallery can show every density on one page.
 */
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { cx } from '../lib/cx.js';
import { applyBrandTokens } from './brand.js';
import { themeModes } from '../tokens.generated.js';
const ThemeContext = createContext({
    mode: 'system',
    accent: 'brand',
    density: 'comfortable',
    motion: 'auto',
    container: null,
});
export function ClavianThemeProvider({ mode = 'system', accent = 'brand', density = 'comfortable', motion = 'auto', brand, brandScope, as: Element = 'div', className, children, }) {
    const [container, setContainer] = useState(null);
    const resolvedMode = useResolvedMode(mode);
    const theme = useMemo(() => ({ mode, accent, density, motion, container }), [mode, accent, density, motion, container]);
    useEffect(() => {
        if (!brand)
            return undefined;
        return applyBrandTokens(brand, brandScope ? { scope: brandScope } : {});
    }, [brand, brandScope]);
    return (_jsx(ThemeContext.Provider, { value: theme, children: _jsx(Element, { ref: setContainer, className: cx('clav-root', className), "data-theme": resolvedMode, "data-accent": accent, "data-density": density, "data-motion": motion === 'none' ? 'none' : undefined, children: children }) }));
}
/**
 * Turns `system` into a real mode once the browser can be asked.
 *
 * Until then it returns undefined and the attribute is left off, so the generated CSS's
 * `prefers-color-scheme` rules decide and there is no flash of the wrong theme on first paint.
 * After mount the attribute is always written, which is what lets a `data-accent` subtree state
 * the mode it belongs to rather than inheriting whatever the OS prefers.
 */
function useResolvedMode(mode) {
    const [system, setSystem] = useState(undefined);
    useEffect(() => {
        if (mode !== 'system' || typeof window.matchMedia !== 'function')
            return undefined;
        const query = window.matchMedia('(prefers-color-scheme: light)');
        // A light OS only gets a light theme once the tokens define one (dark only today, ADR-0023).
        const light = themeModes.find((m) => m === 'light');
        const sync = () => setSystem(query.matches && light ? light : 'dark');
        sync();
        query.addEventListener('change', sync);
        return () => query.removeEventListener('change', sync);
    }, [mode]);
    return mode === 'system' ? system : mode;
}
export function useClavianTheme() {
    return useContext(ThemeContext);
}
/**
 * Where the kit's overlays mount. Undefined outside a provider, which sends Radix back to its
 * default of document.body — correct for an app that themed `<html>` itself.
 */
export function usePortalContainer() {
    return useContext(ThemeContext).container ?? undefined;
}
/**
 * The attributes the provider sets, for a surface that puts them on `<html>` itself — which is what
 * an app should do, so the page background and the scrollbar follow the theme too.
 */
export function themeAttributes(theme) {
    const attributes = {};
    if (theme.mode && theme.mode !== 'system')
        attributes['data-theme'] = theme.mode;
    if (theme.accent)
        attributes['data-accent'] = theme.accent;
    if (theme.density)
        attributes['data-density'] = theme.density;
    if (theme.motion === 'none')
        attributes['data-motion'] = 'none';
    return attributes;
}
//# sourceMappingURL=theme-provider.js.map